import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '@hokm/shared';
import { PERSIAN_THEME } from '../theme/persianTheme';
import { RENDER_SCALE } from '../renderScale';

export interface MysticalTitleOptions {
  centered?: boolean;
  skipParticles?: boolean;
}

const TITLE_Y = 55;
const CENTERED_Y = GAME_HEIGHT / 2 - 50;

/**
 * Creates mystical animated title with candlelight flicker effect
 * and cascading golden dust particles.
 */
export class MysticalTitle {
  private scene: Phaser.Scene;
  private options: MysticalTitleOptions;

  // Container for all title elements (enables position animation)
  private container!: Phaser.GameObjects.Container;

  // Main title elements
  private titleGlow!: Phaser.GameObjects.Text;
  private titleText!: Phaser.GameObjects.Text;
  private hitArea!: Phaser.GameObjects.Rectangle;

  // Farsi subtitle elements
  private subtitleGlow!: Phaser.GameObjects.Text;
  private subtitleText!: Phaser.GameObjects.Text;

  // Cascading dust emitters
  private dustEmitters: Phaser.GameObjects.Particles.ParticleEmitter[] = [];

  // Color cycling state
  private baseGlowAlpha: number = 0.2;

  constructor(scene: Phaser.Scene, options: MysticalTitleOptions = {}) {
    this.scene = scene;
    this.options = options;
    this.create();
  }

  private create(): void {
    // Create container at centered or normal position
    const containerY = this.options.centered ? CENTERED_Y : TITLE_Y;
    this.container = this.scene.add.container(GAME_WIDTH / 2, containerY);

    this.createTitle();
    this.createSubtitle();
    if (!this.options.skipParticles) {
      this.createCascadingDust();
    }
    this.createHitArea();
  }

  /**
   * Create the main HOKM title with glow layer and gradient.
   */
  private createTitle(): void {
    // Glow layer - slightly larger, behind main text (relative to container)
    this.titleGlow = this.scene.add
      .text(0, 0, 'HOKM', {
        fontFamily: PERSIAN_THEME.fonts.title,
        fontSize: '58px',
        color: PERSIAN_THEME.hex.gold,
        stroke: PERSIAN_THEME.hex.gold,
        strokeThickness: 6,
      })
      .setOrigin(0.5)
      .setAlpha(0.2)
      .setScale(1.02);

    // Main title text with vertical gradient (bright gold top to bronze bottom)
    this.titleText = this.scene.add
      .text(0, 0, 'HOKM', {
        fontFamily: PERSIAN_THEME.fonts.title,
        fontSize: '56px',
        stroke: PERSIAN_THEME.hex.feltGreen,
        strokeThickness: 4,
      })
      .setOrigin(0.5);

    // Apply vertical gradient fill (top: bright gold, bottom: bronze)
    // Note: gradient is applied in updateTitleGradient() during update loop
    // Initial fill with solid color as fallback
    this.titleText.setFill(PERSIAN_THEME.hex.gold);

    this.container.add([this.titleGlow, this.titleText]);
  }

  /**
   * Create the Farsi subtitle with glow layer.
   */
  private createSubtitle(): void {
    // Subtitle offset from title (100 - 55 = 45)
    const subtitleY = 45;

    // Glow layer for subtitle
    this.subtitleGlow = this.scene.add
      .text(0, subtitleY, PERSIAN_THEME.farsi.hokm, {
        fontFamily: PERSIAN_THEME.fonts.farsi,
        fontSize: '25px',
        color: PERSIAN_THEME.hex.bronze,
        stroke: PERSIAN_THEME.hex.bronze,
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setAlpha(0.15)
      .setScale(1.01);

    // Main subtitle text
    this.subtitleText = this.scene.add
      .text(0, subtitleY, PERSIAN_THEME.farsi.hokm, {
        fontFamily: PERSIAN_THEME.fonts.farsi,
        fontSize: '24px',
        color: PERSIAN_THEME.hex.bronze,
      })
      .setOrigin(0.5);

    this.container.add([this.subtitleGlow, this.subtitleText]);
  }

  /**
   * Create cascading gold dust particles that fall from the title.
   */
  private createCascadingDust(): void {
    const key = 'gold-dust-particle';
    if (!this.scene.textures.exists(key)) return;

    // Title is at x=640, ~160px wide. Create 3 emitter zones.
    const titleY = 70; // Just below title baseline (title at y=55)
    const zones = [
      { x: GAME_WIDTH / 2 - 50, spread: 25 },
      { x: GAME_WIDTH / 2, spread: 30 },
      { x: GAME_WIDTH / 2 + 50, spread: 25 },
    ];

    for (const zone of zones) {
      const emitter = this.scene.add.particles(zone.x, titleY, key, {
        frequency: 400,
        quantity: 1,
        lifespan: { min: 2000, max: 3500 },
        x: { min: -zone.spread, max: zone.spread },
        y: { min: -5, max: 5 },
        speedY: { min: 8, max: 18 },
        speedX: { min: -4, max: 4 },
        gravityY: 12,
        scale: { start: 0.3 / RENDER_SCALE, end: 0.08 / RENDER_SCALE },
        alpha: { start: 0.45, end: 0 },
        tint: [PERSIAN_THEME.colors.gold, 0xffd700, PERSIAN_THEME.colors.bronze],
        blendMode: Phaser.BlendModes.ADD,
      });

      emitter.setDepth(2);
      this.dustEmitters.push(emitter);
    }
  }

  /**
   * Create invisible hit area for click detection.
   * Clicking navigates to base URL (removes room params).
   */
  private createHitArea(): void {
    // Create invisible rectangle covering the title (relative to container)
    this.hitArea = this.scene.add.rectangle(0, 0, 180, 70, 0x000000, 0);
    this.hitArea.setInteractive({ useHandCursor: true });

    // Hover effect: increase glow intensity
    this.hitArea.on('pointerover', () => {
      this.baseGlowAlpha = 0.4;
    });

    this.hitArea.on('pointerout', () => {
      this.baseGlowAlpha = 0.2;
    });

    // Click: navigate to base URL (removes all query params)
    this.hitArea.on('pointerdown', () => {
      window.location.href = window.location.origin + '/';
    });

    this.container.add(this.hitArea);
  }

  /**
   * Update the flickering animation and color cycling. Call in scene's update loop.
   */
  update(time: number): void {
    // Candlelight flicker - 3 layered oscillations
    const slow = Math.sin(time / 4000) * 0.08;
    const medium = Math.sin(time / 1500) * 0.04;
    const fast = Math.sin(time / 300) * 0.02;
    const titleAlpha = Math.max(0.85, Math.min(1.0, 0.92 + slow + medium + fast));

    // Apply to main title
    this.titleText.setAlpha(titleAlpha);
    this.titleGlow.setAlpha(titleAlpha * this.baseGlowAlpha * 1.25);

    // Color cycling - 18 second cycle through gold/amber/bronze spectrum
    this.updateTitleGradient(time);

    // Subtitle flicker - scaled down amplitude, phase offset
    const subtitleSlow = Math.sin(time / 4000 + 0.5) * 0.05;
    const subtitleMedium = Math.sin(time / 1500 + 0.3) * 0.025;
    const subtitleFast = Math.sin(time / 300 + 0.7) * 0.012;
    const subtitleAlpha = Math.max(0.88, Math.min(1.0, 0.94 + subtitleSlow + subtitleMedium + subtitleFast));

    this.subtitleText.setAlpha(subtitleAlpha);
    this.subtitleGlow.setAlpha(subtitleAlpha * 0.2);
  }

  /**
   * Update title gradient with slow color cycling through gold/amber/bronze.
   */
  private updateTitleGradient(time: number): void {
    // Guard: ensure text and its canvas context are fully initialized
    // Phaser text objects may not have their internal texture ready on first frames
    if (!this.titleText.context || !this.titleText.canvas) return;

    try {
      // 18 second cycle
      const phase = (time / 18000) % 1;

      // Interpolate through color stops: gold -> amber -> bronze -> gold
      // Using HSL-like interpolation for smooth transitions
      const colors = this.getGradientColors(phase);

      const gradient = this.titleText.context.createLinearGradient(0, 0, 0, this.titleText.height);
      gradient.addColorStop(0, colors.top);
      gradient.addColorStop(0.5, colors.mid);
      gradient.addColorStop(1, colors.bottom);
      this.titleText.setFill(gradient);

      // Also update glow color to match
      this.titleGlow.setColor(colors.mid);
    } catch {
      // Text object not fully initialized yet, skip this frame
    }
  }

  /**
   * Calculate gradient colors based on cycle phase.
   * Cycles through: Gold -> Amber -> Bronze -> back to Gold
   */
  private getGradientColors(phase: number): { top: string; mid: string; bottom: string } {
    // Color keyframes (RGB values)
    const keyframes = [
      { top: [255, 215, 0], mid: [212, 175, 55], bottom: [139, 115, 85] }, // Gold
      { top: [255, 191, 0], mid: [218, 165, 32], bottom: [153, 101, 21] }, // Amber
      { top: [205, 127, 50], mid: [184, 115, 51], bottom: [139, 90, 43] }, // Bronze
      { top: [255, 215, 0], mid: [212, 175, 55], bottom: [139, 115, 85] }, // Back to Gold
    ];

    // Find which segment we're in (3 segments for 4 keyframes)
    const segment = phase * 3;
    const index = Math.floor(segment);
    const t = segment - index;

    const from = keyframes[index];
    const to = keyframes[Math.min(index + 1, keyframes.length - 1)];

    // Smooth easing
    const eased = t * t * (3 - 2 * t);

    const lerp = (a: number, b: number) => Math.round(a + (b - a) * eased);
    const toHex = (rgb: number[]) => `#${rgb.map((c) => c.toString(16).padStart(2, '0')).join('')}`;

    return {
      top: toHex([lerp(from.top[0], to.top[0]), lerp(from.top[1], to.top[1]), lerp(from.top[2], to.top[2])]),
      mid: toHex([lerp(from.mid[0], to.mid[0]), lerp(from.mid[1], to.mid[1]), lerp(from.mid[2], to.mid[2])]),
      bottom: toHex([
        lerp(from.bottom[0], to.bottom[0]),
        lerp(from.bottom[1], to.bottom[1]),
        lerp(from.bottom[2], to.bottom[2]),
      ]),
    };
  }

  /**
   * Animate the title to a new Y position.
   * Useful for transitioning from centered to normal position.
   */
  animateToPosition(targetY: number, duration: number = 600): Promise<void> {
    return new Promise((resolve) => {
      this.scene.tweens.add({
        targets: this.container,
        y: targetY,
        duration,
        ease: 'Power2',
        onComplete: () => resolve(),
      });

      // Also animate particles if they exist (they use scene coordinates, not container)
      if (this.dustEmitters.length > 0) {
        const deltaY = targetY - this.container.y;
        this.dustEmitters.forEach((emitter) => {
          this.scene.tweens.add({
            targets: emitter,
            y: emitter.y + deltaY,
            duration,
            ease: 'Power2',
          });
        });
      }
    });
  }

  /**
   * Fade out the title over the specified duration.
   */
  fadeOut(duration: number = 300): void {
    this.scene.tweens.add({
      targets: this.container,
      alpha: 0,
      duration,
      ease: 'Quad.easeOut',
    });

    // Also fade dust emitters
    for (const emitter of this.dustEmitters) {
      this.scene.tweens.add({
        targets: emitter,
        alpha: 0,
        duration,
        ease: 'Quad.easeOut',
      });
    }
  }

  /**
   * Get the container for external manipulation.
   */
  getContainer(): Phaser.GameObjects.Container {
    return this.container;
  }

  /**
   * Clean up all title elements.
   */
  destroy(): void {
    this.container?.destroy();

    for (const emitter of this.dustEmitters) {
      emitter.destroy();
    }
    this.dustEmitters = [];
  }
}
